/* eslint-disable no-else-return */
import React, { useState } from 'react';

import {
  Button,
  ButtonFormik,
  Card,
  CardBody,
  CardHeader,
  CheckboxField,
  Col,
  Container,
  Link,
  Row,
  TextInputField,
  Tooltip,
  Typography,
  useAlertContext,
} from '@bw/alloy-react';
import { Form, Formik } from 'formik';

import * as yup from 'yup';
import parsePhoneNumberFromString from 'libphonenumber-js';
import ExpressHeader from 'components/ExpressHeader';
import { useRegisterAccount } from 'hooks/express/useRegisterAccount';
import composeURL from 'utils/composeURL';

const validateNumber = function (val) {
  if (!val) return true;
  try {
    const phoneNumber = parsePhoneNumberFromString(val, 'US');
    if (!phoneNumber) return false;
    return phoneNumber.isValid() && phoneNumber.number === val; // ensures E.164
  } catch {
    return false;
  }
};

const validationSchema = yup.object({
  tn: yup
    .string()
    .nullable()
    .test(
      'is-valid-phone',
      'Mobile phone number must be in E.164 format. For example: +19195551234',
      validateNumber,
    ),
  terms: yup.boolean().isTrue(),
  firstName: yup.string().required(),
  lastName: yup.string().required(),
  email: yup.string().email().required(),
});

export interface LoginValues {
  username: string;
  password: string;
  rememberme: boolean;
}

const SignUp: React.FC = () => {
  const registerAccount = useRegisterAccount();
  const { alertDanger } = useAlertContext();
  const [loading, setLoading] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const signUp = async (values) => {
    const { tn, firstName, email, lastName } = values;
    try {
      setLoading(true);
      await registerAccount({
        input: { phoneNumber: tn, email, firstName, lastName },
      });

      setLoading(false);
      setSubmittedEmail(email);
    } catch (e) {
      setLoading(false);
      alertDanger(
        e?.data ||
          'Mobile telephone verification failed to send. Please try again.',
      );
    }
  };

  return (
    <Container
      breakpoint="md"
      className="d-flex flex-column flex-grow gap-5 justify-content-center flex-grow-1"
    >
      <ExpressHeader />

      {submittedEmail ? (
        <Card className="p-5" data-bw="email-verification-card">
          <CardHeader title="Verify your email" />
          <CardBody>
            <div className="d-flex flex-column gap-3">
              <Typography>
                There should be an email in your{' '}
                <strong>{submittedEmail}</strong> inbox to verify your email
                address and phone number. (Check your junk folder just in case.)
              </Typography>
              <Typography className="d-flex">
                <span>Didn&apos;t get it? </span>
                <Button
                  icon="forward_to_inbox"
                  textOnly
                  data-bw="send-another-email"
                >
                  send another email
                </Button>{' '}
              </Typography>
              <Typography>
                If you haven&apos;t received anything in the next few minutes,
                visit our
                <Link
                  href="https://support.bandwidth.com/home"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Support Center
                </Link>{' '}
                for assistance.
              </Typography>
            </div>
          </CardBody>
        </Card>
      ) : (
        <Card className="p-5" data-bw="signup-form-card">
          <CardHeader
            title="Start your free trial"
            subHeading="You’ll get your own Bandwidth phone number and some free
                  credits to play around."
          />
          <CardBody>
            <Formik
              initialValues={{
                tn: '',
                firstName: '',
                lastName: '',
                email: '',
              }}
              validationSchema={validationSchema}
              onSubmit={signUp}
              validateOnMount
            >
              <Form className="d-flex flex-column gap-3">
                <Row>
                  <Col>
                    <TextInputField
                      name="firstName"
                      label="First name"
                      required
                    />
                  </Col>
                  <Col>
                    <TextInputField
                      name="lastName"
                      label="Last name"
                      required
                    />
                  </Col>
                </Row>

                <TextInputField
                  name="email"
                  label="Email"
                  type="email"
                  required
                />
                <TextInputField
                  name="tn"
                  label="Your mobile number"
                  helpText={
                    <>
                      US/Can E.164 format, please. Why do we need this?{' '}
                      <Tooltip
                        message="Once we verify this phone number, you’ll be able to use it to send and receive test calls with your Bandwidth phone number."
                        className="tooltip-right-content"
                      />
                    </>
                  }
                  required
                />
                <CheckboxField
                  id="flexCheck"
                  label={
                    <p>
                      I agree to Bandwidth&apos;s{' '}
                      <Link
                        href="https://www.bandwidth.com/legal/product-terms/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms & Conditions
                      </Link>
                    </p>
                  }
                  required
                  name="terms"
                />
                <ButtonFormik
                  loading={loading}
                  style={{ width: 'fit-content' }}
                  data-bw="signup-submit"
                >
                  Let&apos;s go
                </ButtonFormik>
              </Form>
            </Formik>
          </CardBody>
        </Card>
      )}
      <Typography
        className="w-100 justify-content-center d-flex gap-1"
        style={{ maxWidth: 'none', color: 'white' }}
      >
        Already a user?{' '}
        <Link to={composeURL('/login')} className="link-white">
          Log in
        </Link>
      </Typography>
    </Container>
  );
};
export default SignUp;
